import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  VStack,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RiDeleteBinLine } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { DeleteFolderErrorResponse } from '../../interfaces'

interface Props {
  isOpen: boolean
  onClose(): void
}

interface FormProps {
  confirmation: string
}

const EmptyMLModal = ({ isOpen, onClose }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [formError, setFormError] = useState<string>('')
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{
            confirmation: '',
          }}
          onSubmit={async (values, { setFieldError }): Promise<void> => {
            setFormError('')

            if (values.confirmation !== 'DELETE') {
              setFieldError(
                'confirmation',
                t(
                  'please_type_delete_to_confirm',
                  'Please type DELETE in above field',
                ),
              )
              return
            }
            const res = (await request.delete('/api/v2/folder/', {
              folderPath: '/',
            })) as null | DeleteFolderErrorResponse

            if (request.error) {
              setFormError(
                res?.message ??
                  t(
                    'error_in_deleting_all_media',
                    'Error in deleting all media. Please try again after sometime.',
                  ),
              )
            } else {
              toast({
                description: t('all_media_deleted', 'All media deleted.'),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
              setFormError('')
              onClose()
            }
          }}
          validationSchema={Yup.object().shape({
            confirmation: Yup.string().required('Required'),
          })}
        >
          {({ isSubmitting, submitForm }) => (
            <>
              <ModalHeader>
                <Icon
                  as={RiDeleteBinLine}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('empty_media_library', 'Empty media library')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack>
                  <Alert status="error">
                    <AlertIcon />
                    <AlertDescription>
                      <Trans t={t} i18nKey="empty_ml_warning">
                        After submitting, all your files and folder will be
                        permanently deleted from media library. This action is
                        irreversible.
                      </Trans>
                    </AlertDescription>
                  </Alert>
                </VStack>
                {formError ? (
                  <Alert status="error" mt="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="confirmation">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="1em"
                      isInvalid={Boolean(
                        form.errors.confirmation && form.touched.confirmation,
                      )}
                      isRequired
                    >
                      <FormLabel>
                        {t('type_delete_to_confirm', 'Type DELETE to confirm')}
                      </FormLabel>
                      <Input {...field} />
                      {form.errors.confirmation && form.touched.confirmation ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.confirmation}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  colorScheme="appRed"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={async () => {
                    await submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default EmptyMLModal
